import { Injectable } from '@angular/core';
import { ISizeRun } from '@epicuro-next/data/product';
import {
  selectAvailableScaleSets,
  selectAvailableSizeIndexes,
  selectCurrentlyActiveOrderType,
  selectSizeRuns,
} from '@epicuro-next/state/config-strip-state';
import { ComponentStore } from '@ngrx/component-store';
import { select, Store } from '@ngrx/store';
import { asyncScheduler, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ISizeRunsState } from '../models/size-runs-state';

const initialState: ISizeRunsState = {
  sizeRuns: [],
};

@Injectable()
export class SizeRunsService extends ComponentStore<ISizeRunsState> {
  public sizeRuns$ = this.select((state) => state.sizeRuns);
  public hasSizeRuns$ = this.select((state) => !!state.sizeRuns.length);

  constructor(private store: Store) {
    super(initialState);

    asyncScheduler.schedule(() => {
      this.init();
    }, 0);
  }

  private init() {
    combineLatest([
      this.store.pipe(select(selectSizeRuns)),
      this.store.pipe(select(selectAvailableScaleSets())),
      this.store.pipe(select(selectAvailableSizeIndexes)),
      this.store.pipe(select(selectCurrentlyActiveOrderType)),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        ([
          sizeRuns,
          availableScaleSets,
          availableSizeIndexes,
          activeOrderType,
        ]) => {
          const sizeRun: ISizeRun | undefined = sizeRuns
            ? sizeRuns[
                (activeOrderType?.toLowerCase() ?? '') as 'now' | 'future'
              ]
            : undefined;

          if (!sizeRun) {
            this.patchState({
              sizeRuns: [],
            });
            return;
          }

          const productScaleSet = availableScaleSets?.scales[
            sizeRun.scaleSet
          ] ?? { size: [] };

          if (!productScaleSet) {
            this.patchState({
              sizeRuns: [],
            });
            return;
          }

          let productSizeRun = productScaleSet.size.map((sizeKey) => {
            const index = sizeRun.sizeKeys.indexOf(sizeKey);

            return index === -1 ? 0 : sizeRun.sizes[index];
          });

          const hasSomeValue = productSizeRun.some(Boolean);

          if (!hasSomeValue) {
            const [offset] = availableSizeIndexes as Set<number>;
            productSizeRun = sizeRun.sizes.slice(offset);
          }

          this.patchState({
            sizeRuns: productSizeRun,
          });
        },
      );
  }
}
